import { useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError, copyText } from "../../lib/api";
import { Panel } from "./fields";

export const CopyButton = ({ text, testid, label = "Copy" }) => {
  const [done, setDone] = useState(false);
  const click = async () => {
    await copyText(text, toast);
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button type="button" className="btn-outline !py-2" onClick={click} data-testid={testid}>
      {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{label}
    </button>
  );
};

export const snippets = (origin, slug, primary) => {
  const link = `${origin}/s/${slug}`;
  const frame = `eres-booking-${slug}`;
  return {
    link,
    button: `<a href="${link}?src=button" target="_blank" rel="noopener" style="display:inline-block;padding:14px 28px;background:${primary};color:#ffffff;border-radius:999px;font-family:sans-serif;font-weight:600;text-decoration:none">Book now</a>`,
    search: `<div data-booking-search="${slug}"></div>\n<script src="${origin}/api/embed/${slug}/search.js" async></script>`,
    full: `<iframe id="${frame}" src="${link}?src=embed" title="Book online" style="width:100%;min-height:900px;border:0"></iframe>\n<script>window.addEventListener("message",function(e){if(e.origin==="${origin}"&&e.data&&e.data.type==="eres-booking-height"){document.getElementById("${frame}").style.height=e.data.height+"px";}});</script>`,
  };
};

const Snippet = ({ title, hint, code, testid, preview }) => (
  <Panel title={title} hint={hint}>
    {preview && <div className="mb-4 rounded-2xl border border-dashed border-ink/15 bg-white p-5" data-testid={`${testid}-preview`}>{preview}</div>}
    <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-xl bg-ink/[0.04] p-4 font-mono text-xs text-ink/80" data-testid={`${testid}-code`}>{code}</pre>
    <div className="mt-3"><CopyButton text={code} testid={`${testid}-copy`} label="Copy code" /></div>
  </Panel>
);

const OPTIONS = [
  { value: "forward", label: "Forward my address to the booking link", hint: "Visitors to your address land on your booking page. The address bar then shows the booking link." },
  { value: "embed", label: "Put the booking engine on a page at my address", hint: "Your website shows the full booking engine and the address bar keeps your domain." },
  { value: "platform", label: "Use the platform's address", hint: "No setup on your side. Share the booking link below." },
];

const Steps = ({ option, domain, link, platformBase }) => {
  const d = (domain || "book.yourhotel.com").replace(/^https?:\/\//, "").replace(/\/+$/, "");
  if (option === "forward") return (
    <ol className="list-decimal space-y-1 pl-5 text-sm text-ink/70" data-testid="domain-steps-forward">
      <li>Sign in where you manage your domain (your registrar or DNS provider).</li>
      <li>Find "URL forwarding" or "Redirect" and add {d}.</li>
      <li>Forward it to <code className="font-mono">{link}</code>, permanent (301), with HTTPS on.</li>
      <li>Wait up to an hour, then open https://{d} to check.</li>
    </ol>
  );
  if (option === "embed") return (
    <ol className="list-decimal space-y-1 pl-5 text-sm text-ink/70" data-testid="domain-steps-embed">
      <li>Ask your web designer to create a page at https://{d} (or a page on your website such as /book).</li>
      <li>Paste the "Full booking engine" code below into that page.</li>
      <li>Publish the page. The engine resizes itself to fit.</li>
    </ol>
  );
  return (
    <p className="text-sm text-ink/70" data-testid="domain-steps-platform">
      Your booking page is <code className="font-mono">{link}</code>{platformBase ? "" : ". The platform's own address will be used here once it is connected"}.
    </p>
  );
};

const DomainPanel = ({ info, link }) => {
  const [form, setForm] = useState({ domain_option: info.domain_option || "platform", own_domain: info.own_domain || "" });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/domain", form);
      qc.setQueryData(["admin-site"], data);
      setForm({ domain_option: data.domain_option, own_domain: data.own_domain });
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Your own web address" hint="Choose how your own address (for example book.yourhotel.com) leads guests to your booking page.">
      <div className="space-y-3" data-testid="domain-options">
        {OPTIONS.map((o) => (
          <label key={o.value} className={`flex cursor-pointer gap-3 rounded-xl border p-4 ${form.domain_option === o.value ? "border-brand bg-brand-light/40" : "border-ink/10"}`}>
            <input type="radio" name="domain_option" className="mt-1 accent-[rgb(var(--c-primary))]" checked={form.domain_option === o.value}
              onChange={() => setForm({ ...form, domain_option: o.value })} data-testid={`domain-option-${o.value}`} />
            <span><span className="block font-medium">{o.label}</span><span className="text-sm text-ink/60">{o.hint}</span></span>
          </label>
        ))}
      </div>
      {form.domain_option !== "platform" && (
        <label className="field mt-4 max-w-md"><span className="field-label">Your web address</span>
          <input className="field-input font-mono" placeholder="book.yourhotel.com" value={form.own_domain}
            onChange={(e) => setForm({ ...form, own_domain: e.target.value })} data-testid="own-domain-input" />
        </label>
      )}
      <div className="mt-4 rounded-xl bg-ink/[0.03] p-4"><Steps option={form.domain_option} domain={form.own_domain} link={link} platformBase={info.public_base} /></div>
      <button className="btn-primary mt-4" onClick={save} disabled={busy} data-testid="domain-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save choice</button>
    </Panel>
  );
};

export const EmbedTab = ({ info, primary }) => {
  const s = snippets(info.public_base || window.location.origin, info.slug, primary);
  return (
    <div className="space-y-6" data-testid="admin-embed-tab">
      <Panel title="Your booking page link" hint="Share this link or add it to your website menu.">
        <div className="flex flex-wrap items-center gap-3">
          <code className="break-all rounded-xl bg-ink/[0.04] px-4 py-3 font-mono text-sm" data-testid="embed-link">{s.link}</code>
          <CopyButton text={s.link} testid="embed-link-copy" label="Copy link" />
          <a href={s.link} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="embed-link-open"><ExternalLink className="h-4 w-4" />Open</a>
        </div>
      </Panel>
      <DomainPanel info={info} link={s.link} />
      <p className="text-sm text-ink/60">Pick whichever suits your website and paste the code where you want it to appear. Bookings from each one are counted separately in the Bookings tab.</p>
      <Snippet title="Book Now button" hint="A button that opens your booking page in a new tab." code={s.button} testid="embed-button"
        preview={<span dangerouslySetInnerHTML={{ __html: s.button }} />} />
      <Snippet title="Search box" hint="Dates, rooms and guests on your page; guests see available rooms on your booking page." code={s.search} testid="embed-search" />
      <Snippet title="Full booking engine" hint="The complete booking engine inside your page. It resizes itself to fit." code={s.full} testid="embed-full" />
    </div>
  );
};
